import * as React from "react";
import { useTranslation } from 'react-i18next';
import { Routes, Route, useParams, useSearchParams } from "react-router-dom";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import Drawer from "@mui/material/Drawer";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDialogs } from "@toolpad/core/useDialogs";
import SecurityIcon from "@mui/icons-material/Security";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import ShareIcon from "@mui/icons-material/Share";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AppContext from "../../utils/context";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Breadcrumbs from '@mui/material/Breadcrumbs';
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Alert from "@mui/material/Alert";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import moment from "moment";

export default function View() {
  const dialogs = useDialogs();
  const { t, i18n } = useTranslation();
  const appContext = React.useContext(AppContext);
  const [open, setOpen] = React.useState(false);
  const [filters, setFilters] = React.useState({ items: [] });
  const [logs, setLogs] = React.useState([]);
  const [searchParams] = useSearchParams();
  const [currentLog, setCurrentLog] = React.useState(null);

  const helpLogsMsg = t('logs.help.message', { defaultValue: undefined });
  const helpLogsLink = t('logs.help.url', { defaultValue: undefined });

  // let { projectId, instanceId } = useParams();

  React.useEffect(() => {
    loadLogs();
  }, [searchParams]);

  const loadLogs = async () => {
    // const { data, error } = await appContext.database.from("instances").select('*,projects(name)').eq('project_id', projectId);
    const q = appContext.database
      .rpc("get_logs", {})
      .select(
        "id,user_id,workspace_id,workspace_name,project_id,project_name,instance_id,instance_name,service_id,service_name,created_at"
      );
    if (searchParams.get("instance_id")) {
      q.eq("instance_id", searchParams.get("instance_id"));
    }
    if (searchParams.get("service_id")) {
      q.eq("service_id", searchParams.get("service_id"));
    }
    if (searchParams.get("project_id")) {
      q.eq("project_id", searchParams.get("project_id"));
    }
    if (searchParams.get("workspace_id")) {
      q.eq("workspace_id", searchParams.get("workspace_id"));
    }

    const { data, error } = await q;
    setLogs(data);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentLog(null);
  };

  const showDetail = async (row) => {
    const { data, error } = await appContext.database
      .rpc("get_logs")
      .eq("id", row.id)
      .single();
    setCurrentLog(data);
    setOpen(true);
  };

  const columns = [
    {
      field: "created_at",
      headerName: t("commons.date"),
      flex: 1,
      renderCell: (params) => {
        return (
          <>{moment(params.row.created_at).format("YYYY-MM-DD HH:mm:ss")}</>
        );
      },
    },
    {
      field: "user_id",
      headerName: t("commons.user"),
      flex: 1,
    },
    {
      field: "workspace_id",
      headerName: t("commons.workspace"),
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            {params.row.workspace_name}
            <br />
            <small>{params.row.workspace_id}</small>
          </>
        );
      },
    },
    {
      field: "project_id",
      headerName: t("commons.project"),
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            {params.row.project_name}
            <br />
            <small>{params.row.project_id}</small>
          </>
        );
      },
    },
    {
      field: "instance_id",
      headerName: t("commons.instance"),
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            {params.row.instance_name}
            <br />
            <small>{params.row.instance_id}</small>
          </>
        );
      },
    },
    {
      field: "service_id",
      headerName: t("commons.service"),
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            {params.row.service_name}
            <br />
            <small>{params.row.service_id}</small>
          </>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      getActions: (params) => [
        <GridActionsCellItem
          icon={<MoreHorizIcon />}
          label="Details"
          onClick={() => {
            showDetail(params.row);
          }}
        />,
      ],
    },
  ];

  return (
    <div style={{flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0}}>
      <Drawer onClose={handleClose} open={open} anchor="right">
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Typography>
            {t("logs.log_details")}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={handleClose}>{t('buttons.close')}</Button>
        </DialogTitle>

        <DialogContent sx={{ 
            width: {
              xs: "90vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            } }}>

          {helpLogsMsg &&
            <Alert severity="info" onClose={() => { }}
              action=
              {helpLogsLink && <Button color="inherit" size="small" onClick={() => {
                window.open(helpLogsLink, "_blank");
              }}>
                {t('buttons.help')}
              </Button>}
            >
              {helpLogsMsg}
            </Alert>}
          {currentLog && (
            <List sx={{ width: "100%" }}>
              <ListItem>
                <ListItemText
                  primary={t("commons.date")}
                  secondary={
                    <React.Fragment>
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: "text.primary", display: "inline" }}
                      >
                        <>
                          {moment(currentLog.created_at).format(
                            "YYYY-MM-DD HH:mm:ss"
                          )}
                        </>
                      </Typography>
                    </React.Fragment>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("commons.user")}
                  secondary={
                    <React.Fragment>
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: "text.primary", display: "inline" }}
                      >
                        {currentLog.user_id}
                      </Typography>
                    </React.Fragment>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("commons.workspace")}
                  secondary={
                    <React.Fragment>
                      {currentLog.workspace_name && (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ color: "text.primary", display: "block" }}
                        >
                          {currentLog.workspace_name}
                        </Typography>
                      )}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: "text.secondary", display: "block" }}
                      >
                        {currentLog.workspace_id
                          ? currentLog.workspace_id
                          : "N/A"}
                      </Typography>
                    </React.Fragment>
                  }
                />
              </ListItem>
              <ListItem secondaryAction={
                (() => {
                  let tpid = currentLog.project_id || null;
                  if (tpid) {
                    return (
                      <Button edge="end" aria-label="delete"
                        onClick={() => {
                          appContext.router.navigate(`/project/${tpid}`);
                        }}>
                        {t('buttons.show')}
                      </Button>
                    );
                  }
                  return null;
                })()
              }>
                <ListItemText
                  primary={t("commons.project")}
                  secondary={
                    <React.Fragment>
                      {currentLog.project_name && (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ color: "text.primary", display: "block" }}
                        >
                          {currentLog.project_name}
                        </Typography>
                      )}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: "text.secondary", display: "block" }}
                      >
                        {currentLog.project_id ? currentLog.project_id : "N/A"}
                      </Typography>
                    </React.Fragment>
                  }
                />
              </ListItem>
              <ListItem secondaryAction={
                (() => {
                  let tiid = currentLog.instance_id || null;
                  let tpid = currentLog.project_id || null;
                  if (tiid && tpid) {
                    return (
                      <Button edge="end" aria-label="delete"
                        onClick={() => {
                          appContext.router.navigate(`/project/${tpid}?instance_id=${tiid}`);
                        }}>
                        {t('buttons.show')}
                      </Button>
                    );
                  }
                  return null;
                })()
              }>
                <ListItemText
                  primary={t("commons.instance")}
                  secondary={
                    <React.Fragment>
                      {currentLog.instance_name && (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ color: "text.primary", display: "block" }}
                        >
                          {currentLog.instance_name}
                        </Typography>
                      )}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: "text.secondary", display: "block" }}
                      >
                        {currentLog.instance_id
                          ? currentLog.instance_id
                          : "N/A"}
                      </Typography>
                    </React.Fragment>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("commons.service")}
                  secondary={
                    <React.Fragment>
                      {currentLog.service_name && (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ color: "text.primary", display: "block" }}
                        >
                          {currentLog.service_name}
                        </Typography>
                      )}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: "text.secondary", display: "block" }}
                      >
                        {currentLog.service_id ? currentLog.service_id : "N/A"}
                      </Typography>
                    </React.Fragment>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("commons.message")}
                  secondary={
                    <React.Fragment>
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: "text.primary", display: "inline" }}
                      >
                        {currentLog.message}
                      </Typography>
                    </React.Fragment>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText primary={t("commons.data")}
                disableTypography={true}
                  secondary={<>
                    <pre style={{ overflow: "auto" }}>
                      <code>
                        {JSON.stringify(
                          currentLog.data,
                          null,
                          " "
                        )}
                      </code>
                    </pre>
                  </>}>
                </ListItemText>
              </ListItem>
            </List>
          )}
        </DialogContent>
      </Drawer>

      <div className="wrapper" style={{ display: "flex", padding: "20px", minWidth: 0 }}>
        <Paper
          style={{ flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0 }}
        >
          <Box sx={{ padding: "10px", display: "flex", flexDirection: "row" }}>
            <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", paddingY: 1 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography sx={{ color: 'text.primary' }}>{t("logs.title")}</Typography>
              </Breadcrumbs>
            </Box>
          </Box>
          {logs && (
            <DataGrid
              rows={logs}
              columns={columns}
              getRowHeight={() => "auto"}
              initialState={{
                pagination: {
                  paginationModel: {
                    pservicesSize: 50,
                  },
                },
              }}
              disableRowSelectionOnClick={true}
              disableColumnResize={true}
              filterModel={filters}
              onFilterModelChange={(model) => setFilters(model)}
              sx={{ border: "none", flexGrow: 1, minWidth: 0 }}
              onRowDoubleClick={(params) => {
                showDetail(params.row);
              }}
            />
          )}
        </Paper>
      </div>
    </div>
  );
}
